import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DoubleBracketCanvas from "./DoubleBracketCanvas";
import ExportPdfButton from "./ExportPdfButton";
import ScorePopup from "./ScorePopup";
import { useApi } from "../../contexts/ApiContext";
import { useFeatures } from "../../contexts/FeatureContext";
import {
  advanceDouble,
  getChampion,
  getRunnerUp,
  encodeMatch,
  serializeDouble,
} from "../../utils/doubleElimination";
import "./BracketScreen.css";

function DoubleBracketScreen({
  initState,
  tournament,
  onReshuffle,
  onSeededShuffle,
  onReset,
  onComplete,
  backLabel = "← New tournament",
}) {
  const { tournamentsApi, matchResultsApi } = useApi();
  const { flags } = useFeatures();
  const ratingsOn = flags.ratingsEnabled;
  const scoringOn = flags.scoringEnabled;
  const queryClient = useQueryClient();

  const [state, setState] = useState(initState);
  const [pending, setPending] = useState(null); // { matchId, winner, loser, x, y }
  const stageRef = useRef(null);
  useEffect(() => {
    setState(initState);
  }, [initState]);

  const champion = getChampion(state);
  // "Started" once any real (non-bye) match has been decided — locks reshuffle.
  const started = state.matches.some(
    (m) =>
      m.decided && typeof m.team1 === "string" && typeof m.team2 === "string",
  );

  // Auto-save the live bracket so it can be resumed from the Tournaments tab.
  useEffect(() => {
    if (!tournament?.id) return;
    Promise.resolve(
      tournamentsApi.update(tournament.id, {
        ...tournament,
        status: 1,
        format: "double",
        bracketState: JSON.stringify({
          format: "double",
          de: serializeDouble(state),
        }),
      }),
    ).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const recordMatchMutation = useMutation({
    mutationFn: (payload) => matchResultsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matchResults"] });
      queryClient.invalidateQueries({ queryKey: ["players"] });
    },
  });

  const finalizeTournamentMutation = useMutation({
    mutationFn: ({ id, body }) => tournamentsApi.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    },
  });

  function requestPick(matchId, winnerName, e) {
    const m = state.byId[matchId];
    if (!m || m.decided) return;
    const loser = m.team1 === winnerName ? m.team2 : m.team1;
    setPending({ matchId, winner: winnerName, loser, x: e.clientX, y: e.clientY });
  }

  function commitPick(winnerScore, loserScore) {
    const { matchId, winner: winnerName, loser } = pending;
    setPending(null);
    const m = state.byId[matchId];

    const next = advanceDouble(
      state,
      matchId,
      winnerName,
      winnerScore,
      loserScore,
    );
    setState(next);

    if (winnerName && loser && tournament?.id) {
      const { roundNumber, matchIndex } = encodeMatch(m);
      recordMatchMutation.mutate({
        winnerName,
        loserName: loser,
        tournamentId: tournament.id,
        tournamentName: tournament.name,
        roundNumber,
        matchIndex,
        winnerScore,
        loserScore,
      });
    }

  }

  function completeTournament() {
    if (!champion || !tournament?.id) return;
    finalizeTournamentMutation.mutate(
      {
        id: tournament.id,
        body: {
          ...tournament,
          status: 2,
          winner: champion,
          runnerUp: getRunnerUp(state) || "",
          format: "double",
          bracketState: JSON.stringify({
            format: "double",
            de: serializeDouble(state),
          }),
        },
      },
      { onSuccess: () => onComplete && onComplete() },
    );
  }

  return (
    <div>
      <div className="tn-bhead">
        <span className="tn-btitle">BRACKET</span>
        {tournament?.name && (
          <span className="tn-btournament">
            {tournament.name} · Double Elimination
          </span>
        )}
        {onReshuffle && (
          <button
            className="tn-ghost"
            onClick={onReshuffle}
            disabled={started || !!champion}
            title={
              started
                ? "Cannot reshuffle once matches have been played"
                : "Random shuffle"
            }
          >
            ↺ Random
          </button>
        )}
        {onSeededShuffle && ratingsOn && (
          <button
            className="tn-ghost"
            onClick={onSeededShuffle}
            disabled={started || !!champion}
            title={
              started
                ? "Cannot reshuffle once matches have been played"
                : "Seeded shuffle by rating"
            }
          >
            ↺ Seeded
          </button>
        )}
        <ExportPdfButton targetRef={stageRef} filename={tournament?.name} />
        <button className="tn-ghost" onClick={onReset}>
          {backLabel}
        </button>
      </div>

      <DoubleBracketCanvas
        state={state}
        onPick={requestPick}
        stageRef={stageRef}
      />

      <div className="tn-complete-bar">
        <button
          className="tn-complete-btn"
          onClick={completeTournament}
          disabled={!champion}
          title={
            champion
              ? "Mark this tournament complete"
              : "Finish all matches first"
          }
        >
          Complete Tournament
        </button>
      </div>

      {pending && (
        <ScorePopup
          winner={pending.winner}
          loser={pending.loser}
          scoringEnabled={scoringOn}
          position={{ x: pending.x, y: pending.y }}
          onConfirm={commitPick}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

export default DoubleBracketScreen;
